/**
 * A history of entered text for an input box, most recent first.
 * Pair it with useInputHistory (./helpers/useInputHistory) for shell-style up/down recall.
 *
 * Memory-only unless the caller opts into browser storage, because a persisted history outlives
 * a logout on a shared machine. Storage is best-effort: every access is guarded, and when it is
 * missing (Node, prerender), blocked, full or corrupt, the history works from memory instead.
 * A failure is logged once per history, so a blocked storage doesn't flood the console.
 */

export const DEFAULT_CAP = 50;

/**
 * Return a new list with `text` (trimmed) at the front. A repeat moves to the front rather than
 * appearing twice; empty text leaves the list as it was.
 * @param {string[]} entries most recent first
 * @param {string} text
 * @param {number} cap maximum length of the returned list
 * @returns {string[]}
 */
export function addToHistory(entries, text, cap=DEFAULT_CAP) {
    const entry = typeof text === 'string' ? text.trim() : '';
    if (!entry) return entries;
    return [entry, ...entries.filter((e) => e !== entry)].slice(0, cap);
}

// Keep what a well-formed history could contain: trimmed, non-empty strings, each once, at most
// `cap`. Anything else (stale shapes, hand edits) is dropped rather than trusted.
function sanitize(value, cap) {
    if (!Array.isArray(value)) return [];
    const result = [];
    for (const item of value) {
        const entry = typeof item === 'string' ? item.trim() : '';
        if (entry && !result.includes(entry)) result.push(entry);
        if (result.length === cap) break;
    }
    return result;
}

const STORAGE_NAMES = ['localStorage', 'sessionStorage'];

// Absent (Node, prerender) is null. This can throw: see getStorage.
function globalStorage(name) {
    if (name === 'sessionStorage') {
        return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    }
    return typeof localStorage === 'undefined' ? null : localStorage;
}

/**
 * @param {object} options
 * @param {string} options.storageKey storage key holding the JSON array of entries
 * @param {number} [options.cap]
 * @param {'localStorage'|'sessionStorage'|Storage|null} [options.storage] where to persist.
 *   Omitted: memory only, for the life of the page. A name: that browser storage;
 *   sessionStorage is per tab, so tabs don't share entries. A Storage object or null: as is.
 * @returns {{ getEntries: () => string[], record: (text: string) => void,
 *   remove: (text: string) => void, clear: () => void }}
 */
export function createInputHistory({ storageKey, cap=DEFAULT_CAP, storage }={}) {
    // A misspelt name would otherwise fall back to memory without a word.
    if (typeof storage === 'string' && !STORAGE_NAMES.includes(storage)) {
        throw new Error(`createInputHistory: unknown storage '${storage}'`);
    }
    // The entries as last read or written. With no storage this is the history itself; with
    // storage it stands in whenever a read or write fails.
    let memory = [];

    // Once per history: load() runs on every use, so a blocked storage would warn on each one.
    let warned = false;
    function warnOnce(action, error) {
        if (warned) return;
        warned = true;
        console.warn(`Input history '${storageKey}': could not ${action} browser storage; `
            + 'using the in-memory copy.', error);
    }

    function getStorage() {
        if (storage === undefined) return null;
        if (typeof storage !== 'string') return storage;
        // Reading window.localStorage or window.sessionStorage itself throws in some browsers
        // when site data is blocked, so even obtaining it is guarded.
        try {
            return globalStorage(storage);
        } catch (e) {
            warnOnce('open', e);
            return null;
        }
    }

    // A failed write leaves storage behind memory, so memory is the truth until a write succeeds.
    let writeFailed = false;

    // Storage is read on every use (not cached at first use), so entries recorded by another
    // instance on the same key (another tab, for localStorage) are merged, not overwritten.
    function load() {
        const store = getStorage();
        if (store && !writeFailed) {
            try {
                const raw = store.getItem(storageKey);
                memory = raw == null ? [] : sanitize(JSON.parse(raw), cap);
            } catch (e) {
                warnOnce('read', e); // unreadable or corrupt: keep memory
            }
        }
        return memory;
    }

    function save(next) {
        memory = next;
        const store = getStorage();
        if (!store) return;
        try {
            store.setItem(storageKey, JSON.stringify(next));
            writeFailed = false;
        } catch (e) {
            writeFailed = true;
            warnOnce('write', e); // full or blocked: memory still has it
        }
    }

    return {
        getEntries() {
            return [...load()];
        },
        record(text) {
            const next = addToHistory(load(), text, cap);
            if (next !== memory) save(next);
        },
        /** Remove one entry (compared after trimming, like record). */
        remove(text) {
            const entry = typeof text === 'string' ? text.trim() : '';
            const current = load();
            if (current.includes(entry)) save(current.filter((e) => e !== entry));
        },
        clear() {
            save([]);
        },
    };
}
